/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useEffect, useMemo, useState} from 'react';
import {Button, Descriptions, Input, Space, Image} from "antd";
import {DescriptionsItemType} from "antd/es/descriptions";
import styles from './UserVerificationTab.module.scss';
import {getVerificationTag} from "../UserListPanel";
import {useMessageStore} from "../../../../stores/messageStore";
import {User} from "../../../../types/User";
import {useMediaQuery} from "react-responsive";
import {UserCar} from "../../../../types/UserCar";
import {useStore} from "zustand";
import userCarAPI from "../../../../api/userCarAPI";
import {VerifyStatusType} from "../../../../types/Type";
import apiClient from "../../../../api/apiClient";
import {useAppStore} from "../../../../stores/appStore";

interface UserVerificationTabProps {
	shouldUserListPanelRerender: boolean;
	selectedItem: User | undefined;
	afterSubmit: () => any;
	verifyAll: () => any;
}

const UserVerificationTab = ({
	                             shouldUserListPanelRerender,
	                             selectedItem,
	                             afterSubmit,
	                             verifyAll,
                             }: UserVerificationTabProps) => {
	const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' });
	const { setMessages } = useStore(useMessageStore);
	const setShouldShowSpinner = useAppStore((state) => state.setShouldShowSpinner);

	const [businessNumber, setBusinessNumber] = useState('');
	const [userCars, setUserCars] = useState<UserCar[]>([]);
	const [carNumbers, setCarNumbers] = useState<string[]>([]);

	useEffect(() => {
		if (!selectedItem?.id) {
			setBusinessNumber('');
			setUserCars([]);
			setCarNumbers([]);
			return;
		}

		setBusinessNumber(selectedItem.businessNumber || '');

		userCarAPI.fetchUserCarList(selectedItem.id)
			.then((res) => {
				const activeCars = res.filter((item) => !item.isDel);
				setUserCars(activeCars || []);
				setCarNumbers(activeCars.map((c) => c.number || ''));
			})
			.catch((err) => {
				console.error('fetchUserCarList error', err);
				setUserCars([]);
				setCarNumbers([]);
			});
	}, [selectedItem?.id, shouldUserListPanelRerender]);

	const onChangeCarStatus = async (userCarId: string, status: VerifyStatusType, carNumber: string) => {
		if (selectedItem?.id) {
			setShouldShowSpinner(true);
			try {
				await apiClient.put(`/api/admin/business/user/car-image/verify`, {
					targetUserId: selectedItem.id,
					userCarId,
					status,
				});

				if (status === 'verified') setMessages('success', `차량 인증 완료: ${carNumber}`);
				if (status === 'rejected') setMessages('success', `차량 거절 완료: ${carNumber}`);

				await afterSubmit();
			} catch (e) {
				console.error(e);
			} finally {
				setShouldShowSpinner(false);
			}
		}
	};

	const onEditCarNumber = async (userCarId: string, newCarNumber: string) => {
		if (!selectedItem?.id) return;
		setShouldShowSpinner(true);
		try {
			const res = await apiClient.put(`/api/admin/business/user/${selectedItem.id}/car/${userCarId}/${newCarNumber}`);
			setMessages('success', `차량 번호 변경 완료 ${newCarNumber}`);

			await afterSubmit();
		} catch (e) {
			console.error(e);
		} finally {
			setShouldShowSpinner(false);
		}
	};

	const onChangeBusinessStatus = async (businessStatus: VerifyStatusType) => {
		if (selectedItem?.id) {
			setShouldShowSpinner(true);
			try {
				await apiClient.put(`/api/admin/business/user/business-image/verify`, {
					targetUserId: selectedItem.id,
					businessStatus,
				});

				if (businessStatus === 'verified') setMessages('success', `사업자등록증 인증 완료: ${selectedItem.name}`);
				if (businessStatus === 'rejected') setMessages('success', `사업자등록증 거절 완료: ${selectedItem.name}`);

				await afterSubmit();
			} catch (e) {
				console.error(e);
			} finally {
				setShouldShowSpinner(false);
			}
		}
	};

	const onEditBusinessNumber = async (businessNumber: string) => {
		if (!selectedItem?.id) return;
		try {
			await apiClient.put(`/api/admin/business/user/${selectedItem.id}/business/${businessNumber}`);
			setMessages('success', `사업자등록증 번호 변경 완료 ${businessNumber}`);

			await afterSubmit();
		} catch (e) {
			console.error(e);
		}
	};

	const verifyOrRejectButton = (
		onVerify: () => void,
		onReject: () => void,
	) => (
		<Space style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
			<Button size="middle" type="primary" onClick={onVerify}>
				인증
			</Button>
			<Button size="middle" danger onClick={onReject}>
				거절
			</Button>
		</Space>
	);

	const items: DescriptionsItemType[] = useMemo(() => {
		if (!selectedItem) return [];

		const baseList: DescriptionsItemType[] = [
			{
				key: 'businessPhotoUrl',
				label: (
					<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
						<span>사업자등록증 사진</span>
						<span>{getVerificationTag(selectedItem.businessStatus)}</span>
					</div>
				),
				children: (
					selectedItem.businessPhotoUrl
						? (
							<>
								<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '20px' }}>
									<Input
										value={businessNumber}
										onChange={(e) => setBusinessNumber(e.target.value)}
										style={{ width: 150 }}
									/>
									<Button onClick={() => onEditBusinessNumber(businessNumber)}>수정</Button>
									{selectedItem.businessStatus === 'review' ? verifyOrRejectButton(
										() => selectedItem.id && onChangeBusinessStatus('verified'),
										() => selectedItem.id && onChangeBusinessStatus('rejected')
									) : null}
								</div>

								<div style={{ marginTop: '10px', paddingBottom: '40px' }}>
									<Image
										src={selectedItem.businessPhotoUrl}
										alt="사업자등록증 사진"
										style={isTabletOrMobile ? { width: '100%', height: '100%', objectFit: 'contain', cursor: 'pointer' } : { width: '650px', height: '650px', objectFit: 'contain', cursor: 'pointer' }}
									/>
								</div>
							</>
						)
						: '(사진 없음)'
				),
			},
		];

		const carList: DescriptionsItemType[] = userCars.map((item, index) => ({
			key: `userCar${index}`,
			label: (
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
					<span>{`차량 ${index + 1} (${item.number || ''}) (${item.carType || ''})`}</span>
					<span>{getVerificationTag(item.status)}</span>
				</div>
			),
			children: item.photoUrl ? (
				<div style={{ paddingBottom: '40px' }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
						<Input
							value={carNumbers[index] ?? ''}
							onChange={(e) => {
								const copy = [...carNumbers];
								copy[index] = e.target.value;
								setCarNumbers(copy);
							}}
							style={{ width: 150 }}
						/>
						<Button onClick={() => onEditCarNumber(item.id, carNumbers[index])}>수정</Button>
						{item.status === 'review' ? verifyOrRejectButton(
							() => onChangeCarStatus(item.id, 'verified', carNumbers[index] ?? item.number),
							() => onChangeCarStatus(item.id, 'rejected', carNumbers[index] ?? item.number)
						) : null}
					</div>

					<Image
						src={item.photoUrl}
						alt={`${index + 1}번째 차량 사진`}
						style={isTabletOrMobile ? { width: '100%', height: '100%', objectFit: 'contain', cursor: 'pointer' } : { width: '650px', height: '650px', objectFit: 'contain', cursor: 'pointer' }}
					/>
				</div>
			) : '(사진 없음)',
		}));

		return [...baseList, ...carList];
	}, [selectedItem, userCars, businessNumber, carNumbers, isTabletOrMobile, onChangeBusinessStatus, onChangeCarStatus]);

	return (
		<div className={styles.wrap}>
			{(selectedItem?.businessStatus === 'review' || selectedItem?.reviewCount !== "0") && (
				<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
					<Button
						color="primary"
						variant="solid"
						onClick={async () => {
							await verifyAll();
							await afterSubmit();
						}}
					>
						일괄 인증 처리
					</Button>
				</div>
			)}
			<Descriptions
				bordered
				layout="vertical"
				column={1}
				items={items}
			/>
		</div>
	);
};

export default UserVerificationTab;
